<script lang="ts" setup>
import { ref, computed, watch, onMounted, shallowRef, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { DateTime } from 'luxon';
import { useLocalization } from '@/plugins/localization';
import type { TimetableItem, TimetableDocument } from '@/modules/core/common/services/GridService';
import { TimeRange, TimeScale } from '@/modules/low-code/components/helpers/TimetableDataHelper';
import { getColor, getTextColorForBackground } from '@/modules/core/home/inc/helpers';

// -------------------------------------------------------
// Types
// -------------------------------------------------------
interface Settings {
    start: string;
    end: string;
    timeScale: TimeScale;
    timeRange: string;
}

interface Unit {
    active: boolean;
    name: string | number;
    date: string;
    isHoliday: boolean;
    isWeekend: boolean;
    value?: string | number;
}

// -------------------------------------------------------
// Props
// -------------------------------------------------------
const props = defineProps({
  "licence": null,
  "items": null,
  "holidays": null,
  "settings": null,
  "maxHeight": { default: '100%' },
  "readOnly": { type: Boolean, default: false },
  "legend": { default: () => [] }
});

const shallowItems = shallowRef(props.items);

// -------------------------------------------------------
// Deps
// -------------------------------------------------------
const router = useRouter();
const { $t, $i18n } = useLocalization();

// -------------------------------------------------------
// Constants
// -------------------------------------------------------
const COLUMN_WIDTH = 40; // px
const ROW_MIN_HEIGHT = 40; // px
const BUFFER_ROWS = 2;

// -------------------------------------------------------
// Refs & State
// -------------------------------------------------------
const containerRef = ref<HTMLDivElement | null>(null);
const wrapperRef = ref<HTMLDivElement | null>(null);

const containerSize = ref({ width: 0, height: 0 });
const containerScrollLeft = ref(0);
const containerScrollTop = ref(0);

// -------------------------------------------------------
// Helpers
// -------------------------------------------------------
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max));

function sum(arr: number[], start = 0, end = arr.length): number
{
    let s = 0;

    for (let i = start; i < end; i++)
    {
        s += arr[i];
    }

    return s;
}

function firstIndexByCumulativeHeight(heights: number[], target: number): number
{
    let cum = 0;

    for (let i = 0; i < heights.length; i++)
    {
        cum += heights[i];

        if (target < cum)
        {
            return i;
        }
    }

    return heights.length - 1;
}

// -------------------------------------------------------
// Dates & ranges
// -------------------------------------------------------
const startDate = computed(() => DateTime.fromISO(props.settings?.start));
const endDate = computed(() => DateTime.fromISO(props.settings?.end));

const isMonthsShown = computed(() => [TimeRange.Quarterly, TimeRange.HalfYearly, TimeRange.Yearly, TimeRange.CustomDate].includes(props.settings.timeRange) && !(props.settings.timeScale === TimeScale.Week || props.settings.timeScale === TimeScale.Month));
const headerHeight = computed(() => (isMonthsShown.value ? ROW_MIN_HEIGHT * 2 : ROW_MIN_HEIGHT));

// -------------------------------------------------------
// Virtual widths/heights
// -------------------------------------------------------
const timetableStyles = computed(() => ({
    '--timetable-column-width': `${COLUMN_WIDTH}px`,
    '--timetable-row-height': `${ROW_MIN_HEIGHT}px`,
} as Record<string, string>));

const weeks = computed(() =>
{
    const arr: string[] = [];
    let cur = startDate.value;

    while (cur <= endDate.value)
    {
        arr.push(cur.startOf('week').toISODate());
        cur = cur.plus({ weeks: 1 });
    }

    return arr;
});

const months = computed<{ month: string; year: number; days: number; index: number }[]>(() =>
{
    const names = [
        $t('[[[Styczeń]]]'), $t('[[[Luty]]]'), $t('[[[Marzec]]]'), $t('[[[Kwiecień]]]'), $t('[[[Maj]]]'), $t('[[[Czerwiec]]]'),
        $t('[[[Lipiec]]]'), $t('[[[Sierpień]]]'), $t('[[[Wrzesień]]]'), $t('[[[Październik]]]'), $t('[[[Listopad]]]'), $t('[[[Grudzień]]]'),
    ];

    const out: { month: string; year: number; days: number }[] = [];
    let cur = startDate.value.startOf('month');

    while (cur <= endDate.value)
    {
        out.push({ month: names[cur.month - 1], year: cur.year, days: cur.daysInMonth, index: cur.month - 1 });
        cur = cur.plus({ months: 1 });
    }

    return out;
});

function getPageRangeLabel(pageUnits: Unit[]): string
{
    if (!pageUnits.length) return '';

    // Filtrujemy puste jednostki, aby używać tylko rzeczywistych dat
    const realUnits = pageUnits.filter(u => !(u as any).isEmpty && u.date);

    if (!realUnits.length) return '';

    const from = DateTime.fromISO(realUnits[0].date);
    const to = DateTime.fromISO(realUnits[realUnits.length - 1].date);

    const fmt = 'dd.LL.yyyy';

    if (from.hasSame(to, 'day'))
    {
        return from.toFormat(fmt);
    }

    return `${from.toFormat(fmt)} - ${to.toFormat(fmt)}`;
}

function getChunkMonthRanges(chunkUnits: Unit[]): Array<{ month: string; startIndex: number; endIndex: number; colspan: number }>
{
    if (!chunkUnits.length) return [];

    const monthNames = [
        $t('[[[Styczeń]]]'), $t('[[[Luty]]]'), $t('[[[Marzec]]]'), $t('[[[Kwiecień]]]'), $t('[[[Maj]]]'), $t('[[[Czerwiec]]]'),
        $t('[[[Lipiec]]]'), $t('[[[Sierpień]]]'), $t('[[[Wrzesień]]]'), $t('[[[Październik]]]'), $t('[[[Listopad]]]'), $t('[[[Grudzień]]]'),
    ];

    const ranges: Array<{ month: string; startIndex: number; endIndex: number; colspan: number }> = [];
    let currentMonth: number | null = null;
    let startIndex = 0;

    for (let i = 0; i < chunkUnits.length; i++)
    {
        const unit = chunkUnits[i];
        let month: number | null = null;

        if (!(unit as any).isEmpty && unit.date)
        {
            const dt = DateTime.fromISO(unit.date);

            month = dt.month;
        }
        else if (currentMonth !== null)
        {
            // Dla pustych kolumn używamy miesiąca poprzedniej niepustej kolumny
            month = currentMonth;
        }

        if (month === null) continue; // Jeśli nie ma miesiąca i nie było poprzedniego, pomiń

        if (currentMonth === null)
        {
            currentMonth = month;
            startIndex = i;
        }
        else if (currentMonth !== month && !(unit as any).isEmpty)
        {
            // Zmiana miesiąca (tylko dla niepustych kolumn) - zapisz poprzedni zakres
            ranges.push({
                month: monthNames[currentMonth - 1],
                startIndex,
                endIndex: i - 1,
                colspan: i - startIndex,
            });

            // Rozpocznij nowy zakres
            currentMonth = month;
            startIndex = i;
        }
    }

    // Zapisz ostatni zakres
    if (currentMonth !== null)
    {
        ranges.push({
            month: monthNames[currentMonth - 1],
            startIndex,
            endIndex: chunkUnits.length - 1,
            colspan: chunkUnits.length - startIndex,
        });
    }

    return ranges;
}

const units = computed<Unit[]>(() =>
{
    let result: Unit[];

    if (props.settings.timeScale === TimeScale.Month)
    {
        const nowMonth = DateTime.utc().month;

        result = months.value.map(m => ({
            value: m.index + 1,
            active: m.index + 1 === nowMonth,
            name: m.month.slice(0, 3),
            date: `${m.year}-${String(m.index + 1).padStart(2, '0')}-01`,
            isHoliday: false,
            isWeekend: false,
        }));

        return result;
    }
    else if (props.settings.timeScale === TimeScale.Week)
    {
        const nowWeek = DateTime.utc().weekNumber;

        result = weeks.value.map((wISO) =>
        {
            const dt = DateTime.fromISO(wISO);
            const weekNum = dt.weekNumber;

            return {
                active: weekNum === nowWeek,
                name: weekNum,
                date: wISO,
                isHoliday: false,
                isWeekend: false,
                value: weekNum,
            } as Unit;
        });
    }
    else
    {
        const startDt = startDate.value;
        const endDt = endDate.value;
        const diff = Math.floor(endDt.diff(startDt, 'days').days ?? 0);

        const todayISO = DateTime.utc().toISODate();

        result = new Array(diff + 1);

        for (let i = 0; i <= diff; i++)
        {
            const cur = startDt.plus({ days: i });
            const iso = cur.toISODate()!;

            const dayValue = iso.slice(-2);

            result[i] = {
                value: dayValue,
                active: iso === todayISO,
                name: getUnitName(iso),
                date: iso,
                isHoliday: isHoliday(iso),
                isWeekend: isWeekend(iso),
            } as Unit;
        }
    }

    return result;
});

const viewportWidth = computed(() => units.value.length * COLUMN_WIDTH);

interface PrintChunk
{
    monthLabel: string;
    units: Unit[];
}

const printChunks = computed<PrintChunk[]>(() =>
{
    const chunks: PrintChunk[] = [];

    if (!units.value.length) return chunks;

    const COLUMNS_PER_PAGE = 18;

    const monthNames = [
        $t('[[[Styczeń]]]'), $t('[[[Luty]]]'), $t('[[[Marzec]]]'), $t('[[[Kwiecień]]]'), $t('[[[Maj]]]'), $t('[[[Czerwiec]]]'),
        $t('[[[Lipiec]]]'), $t('[[[Sierpień]]]'), $t('[[[Wrzesień]]]'), $t('[[[Październik]]]'), $t('[[[Listopad]]]'), $t('[[[Grudzień]]]'),
    ];

    // Dzielimy jednostki na strony po 18 kolumn, bez sprawdzania zmian miesiąca
    for (let offset = 0; offset < units.value.length; offset += COLUMNS_PER_PAGE)
    {
        const slice = units.value.slice(offset, offset + COLUMNS_PER_PAGE);

        if (!slice.length) continue;

        // Etykieta miesiąca z pierwszego dnia na stronie
        const dt = DateTime.fromISO(slice[0].date);
        const label = monthNames[dt.month - 1];

        const pageUnits: Unit[] = [...slice];

        // Jeśli ostatnia strona ma mniej niż 18 kolumn - dopełnij pustymi
        if (pageUnits.length < COLUMNS_PER_PAGE)
        {
            const emptyUnitsNeeded = COLUMNS_PER_PAGE - pageUnits.length;
            const lastDate = pageUnits[pageUnits.length - 1]?.date;

            if (lastDate)
            {
                for (let j = 0; j < emptyUnitsNeeded; j++)
                {
                    const emptyDate = DateTime.fromISO(lastDate).plus({ days: j + 1 }).toISODate();

                    if (emptyDate)
                    {
                        pageUnits.push({
                            date: emptyDate,
                            name: '',
                            value: '',
                            active: false,
                            isWeekend: false,
                            isHoliday: false,
                            isEmpty: true, // Flaga dla pustych kolumn
                        } as Unit);
                    }
                }
            }
            else
            {
                // Jeśli nie ma daty, użyj pierwszej daty z chunk lub dzisiejszej daty
                const baseDate = pageUnits[0]?.date || DateTime.now().toISODate();

                if (baseDate)
                {
                    for (let j = 0; j < emptyUnitsNeeded; j++)
                    {
                        const emptyDate = DateTime.fromISO(baseDate).plus({ days: pageUnits.length + j }).toISODate();

                        if (emptyDate)
                        {
                            pageUnits.push({
                                date: emptyDate,
                                name: '',
                                value: '',
                                active: false,
                                isWeekend: false,
                                isHoliday: false,
                                isEmpty: true, // Flaga dla pustych kolumn
                            } as Unit);
                        }
                    }
                }
            }
        }

        chunks.push({
            monthLabel: label,
            units: pageUnits,
        });
    }

    return chunks;
});

// -------------------------------------------------------
// Rows & heights
// -------------------------------------------------------
const rows = computed(() =>
{
    const result = shallowItems.value.flatMap(item => [{ key: item.key, value: item.value, isGroup: true }, ...item.groups.map(group => ({ groupName: item.key, ...group }))]);

    return result;
});

function toUtcDateTime(value: unknown): DateTime | null
{
    if (!value) return null;

    if (DateTime.isDateTime(value))
    {
        return value.toUTC();
    }

    if (value instanceof Date)
    {
        const dt = DateTime.fromJSDate(value, { zone: 'utc' });

        return dt.isValid ? dt : null;
    }

    if (typeof value === 'string')
    {
        const dt = DateTime.fromISO(value, { zone: 'utc' });

        return dt.isValid ? dt : null;
    }

    return null;
}

function normalizeDocumentRange(doc: TimetableDocument): { from: DateTime; to: DateTime } | null
{
    const from = toUtcDateTime(doc.dateFromUtc);
    const rawTo = toUtcDateTime(doc.dateToUtc);

    if (!from || !rawTo) return null;

    const isDateOnlyEnd = rawTo.toMillis() === rawTo.startOf('day').toMillis();
    const to = isDateOnlyEnd ? rawTo.endOf('day') : rawTo;

    return { from, to };
}

function isSameColumn(a: {from: DateTime, to: DateTime}, b: {from: DateTime, to: DateTime})
{
    // check if a and b date ranges overlap
    return a.to.toMillis() >= b.from.toMillis() && a.from.toMillis() <= b.to.toMillis();
}

const rowHeightMap = computed<number[]>(() =>
{
    const result: number[] = [];

    for (const company of shallowItems.value)
    {
        result.push(ROW_MIN_HEIGHT);

        for (const group of company.groups)
        {
            const docs = group.documents
                .map((d) => normalizeDocumentRange(d as TimetableDocument))
                .filter((d): d is { from: DateTime; to: DateTime } => !!d)
                .sort((a, b) => a.from.toMillis() - b.from.toMillis());

            const active: {from: DateTime, to: DateTime}[] = [];
            let maxOverlap = 0;

            for (const doc of docs)
            {
                for (let i = active.length - 1; i >= 0; i--)
                {
                    if (!isSameColumn(active[i], doc))
                    {
                        active.splice(i, 1);
                    }
                }

                active.push(doc);

                if (active.length > maxOverlap)
                {
                    maxOverlap = active.length;
                }
            }

            result.push(Math.max(ROW_MIN_HEIGHT, (maxOverlap || 1) * ROW_MIN_HEIGHT));
        }
    }

    return result;
});

const viewportHeight = computed(() => rowHeightMap.value.reduce((a, b) => a + b, 0));

// Vertical virtualization
const startScrollIndex = computed(() => firstIndexByCumulativeHeight(rowHeightMap.value, containerScrollTop.value));
const topOffset = computed(() => sum(rowHeightMap.value, 0, startScrollIndex.value));

const visibleRowCount = computed(() =>
{
    const available = (wrapperRef.value?.clientHeight ?? 0) - headerHeight.value;
    let s = 0, c = 0;

    for (let i = startScrollIndex.value; i < rowHeightMap.value.length; i++)
    {
        s += rowHeightMap.value[i];

        if (s > available) break;

        c++;
    }

    const result = c + BUFFER_ROWS;

    return result;
});

const visibleRows = computed(() =>
{
    return rowHeightMap.value.slice(startScrollIndex.value, startScrollIndex.value + visibleRowCount.value);
});

// Horizontal virtualization
const viewportCount = computed(() =>
{
    return Math.ceil((containerSize.value.width || 0) / COLUMN_WIDTH);
});
const viewportShift = computed(() =>
{
    return Math.floor(containerScrollLeft.value / COLUMN_WIDTH);
});
const clampedShift = computed(() =>
{
    return clamp(viewportShift.value, 0, Math.max(0, units.value.length - viewportCount.value));
});

const windowUnits = computed(() =>
{
    return units.value.slice(clampedShift.value, Math.min(units.value.length, clampedShift.value + viewportCount.value + 1));
});

// -------------------------------------------------------
// Documents by day
// -------------------------------------------------------
function getColumnKey(date: DateTime, mode: TimeScale)
{
    if (mode === TimeScale.Day)
        return date.toUTC().toISODate() ?? '';

    if (mode === TimeScale.Week)
        return date.toUTC().startOf('week').toISODate() ?? '';

    if (mode === TimeScale.Month)
        return date.toUTC().startOf('month').toISODate() ?? '';

    return '';
}

function alignToScale(date: DateTime, mode: TimeScale): DateTime
{
    if (mode === TimeScale.Day) return date.startOf('day');

    if (mode === TimeScale.Week) return date.startOf('week');

    if (mode === TimeScale.Month) return date.startOf('month');

    return date;
}

function nextScaleDate(date: DateTime, mode: TimeScale): DateTime
{
    if (mode === TimeScale.Day) return date.plus({ days: 1 });

    if (mode === TimeScale.Week) return date.plus({ weeks: 1 });

    if (mode === TimeScale.Month) return date.plus({ months: 1 });

    return date;
}

function getDocumentSeriesKey(doc: TimetableDocument): string
{
    // Keep intentional duplicates from different series (e.g. same publicId, different color)
    // while still allowing dedupe of accidental repeats from range stepping.
    return [
        doc.publicId ?? '',
        doc.color ?? '',
        doc.shortName ?? '',
        doc.title ?? '',
        doc.dateFromUtc?.toString?.() ?? '',
        doc.dateToUtc?.toString?.() ?? '',
    ].join('|');
}

function buildDocumentsByDates(visibleDates: string[])
{
    const map = new Map<string, { key: string; items: TimetableDocument[] }[]>();
    const dedupeKeys = new Set<string>();

    if (!visibleDates.length) return map;

    const visibleFrom = DateTime.fromISO(visibleDates[0], { zone: 'utc' }).startOf('day');
    const lastVisible = DateTime.fromISO(visibleDates[visibleDates.length - 1], { zone: 'utc' }).startOf('day');
    let visibleTo = lastVisible.endOf('day');

    if (props.settings.timeScale === TimeScale.Day)
        visibleTo = lastVisible.endOf('day');
    else if (props.settings.timeScale === TimeScale.Week)
        visibleTo = lastVisible.plus({ days: 6 }).endOf('day');
    else if (props.settings.timeScale === TimeScale.Month)
        visibleTo = lastVisible.endOf('month');

    let groupName = '';

    for (const row of rows.value)
    {
        if (row.isGroup)
        {
            groupName = row.key;
            continue;
        }

        if (!row.documents) continue;

        const key = `${groupName}-${(row as any).key}`;

        for (const doc of row.documents as TimetableDocument[])
        {
            const range = normalizeDocumentRange(doc);

            if (!range) continue;

            if (range.to.toMillis() < visibleFrom.toMillis() || range.from.toMillis() > visibleTo.toMillis()) continue;

            const start = range.from.toMillis() > visibleFrom.toMillis() ? range.from : visibleFrom;
            const end = range.to.toMillis() < visibleTo.toMillis() ? range.to : visibleTo;
            let current = alignToScale(start, props.settings.timeScale);

            while (current.toMillis() <= end.toMillis())
            {
                const dateStr = getColumnKey(current, props.settings.timeScale);
                const dedupeKey = `${dateStr}|${key}|${getDocumentSeriesKey(doc)}`;

                if (!dateStr || dedupeKeys.has(dedupeKey))
                {
                    current = nextScaleDate(current, props.settings.timeScale);
                    continue;
                }

                dedupeKeys.add(dedupeKey);

                if (!map.has(dateStr))
                {
                    map.set(dateStr, []);
                }

                const arr = map.get(dateStr)!;
                let entry = arr.find(e => e.key === key);

                if (!entry)
                {
                    entry = { key, items: [] };
                    arr.push(entry);
                }

                entry.items.push(doc);
                current = nextScaleDate(current, props.settings.timeScale);
            }
        }
    }

    return map;
}

const documentsByDate = computed(() =>
{
    return buildDocumentsByDates(windowUnits.value.map(u => u.date));
});

const printDocumentsByPage = computed(() =>
{
    return printChunks.value.map(chunk => buildDocumentsByDates(chunk.units.map(u => u.date)));
});


// -------------------------------------------------------
// UI helpers
// -------------------------------------------------------
function getUnitName(value: string, format = 'yyyy-MM-dd'): string | number
{
    if (!value || !format) return '';

    if (props.settings.timeScale === TimeScale.Week)
    {
        return DateTime.fromISO(value).weekNumber;
    }

    const day = new Date(value).toLocaleDateString($i18n.shortLocale(), { weekday: 'short' });
    const result = (day.charAt(0).toUpperCase() + day.slice(1)).slice(0, 2);

    return result;
}

function isWeekend(unit: string): boolean
{
    const date = DateTime.fromISO(unit);

    return date.weekday === 6 || date.weekday === 7;
}

function isHoliday(unit: string): boolean
{
    const date = DateTime.fromISO(unit);
    const result = props.holidays.some(h => DateTime.fromISO(h.date).toISODate() === date.toISODate());

    return result;
}

function getHolidayName(unit: string): string
{
    const holiday = props.holidays.find(h => DateTime.fromISO(h.date).toISODate() === DateTime.fromISO(unit).toISODate());

    return holiday?.name || '';
}

function handleResize()
{
    containerSize.value.width = containerRef.value?.clientWidth ?? 0;
    containerSize.value.height = containerRef.value?.clientHeight ?? 0;
}

let scrollRafId = 0;

function handleScroll(e: Event)
{
    const el = e.target as HTMLDivElement;

    if (scrollRafId) cancelAnimationFrame(scrollRafId);

    const left = Math.floor(el.scrollLeft);
    const top = el.scrollTop;

    scrollRafId = requestAnimationFrame(() =>
    {
        containerScrollLeft.value = left;
        containerScrollTop.value = top;
    });
}

function onRowClick(item: any)
{
    if (!item.publicId || props.readOnly) return;

    const licence = item.licence ?? props.licence;

    router.push({ name: 'module-form-details', params: { licence, publicId: item.publicId } });
}

function getTooltip(doc: TimetableDocument)
{
    const title = doc.title || '';
    const description = (doc.description || [])
        .filter((i: any) => i?.value)
        .map((i: any) => `${i.key}: ${i.value || '-'}\n`)
        .join('');

    return `${title}${title && description ? `\n\n` : ''}${description}`;
}

watch(units, (value) =>
{
    nextTick(() =>
    {
        handleResize();
    });
});

function delayedHandleResize()
{
    for (let i = 0; i < 10; i++)
    {
        setTimeout(() =>
        {
            handleResize();
        }, 50 * i);
    }
}

watch(() => props.items, (value) =>
{
    shallowItems.value = value;
}, { deep: true });

// -------------------------------------------------------
// Lifecycle
// -------------------------------------------------------
onMounted(() =>
{
    handleResize();
    window.addEventListener('resize', delayedHandleResize);
});

onUnmounted(() =>
{
    window.removeEventListener('resize', delayedHandleResize);
});

const focusedDocuments = ref<string[]>([]);
const rowActionsRef = ref(null);

async function focusDocument(date: string, doc: TimetableDocument)
{
    focusedDocuments.value.push(`${date}-${doc.publicId}-${doc.color}`);

    await nextTick();

    const foundRowAction = rowActionsRef.value.find(r => r.id === doc.publicId && r.settings?.date === date && r.settings?.color === doc.color);

    if (foundRowAction)
    {
        await foundRowAction.fetchActions?.();
        foundRowAction.dropdown?.show?.();
    }
}
</script>

<template>
    <div v-if="!rows.length" class="timetable-grid">
        <IdeoAlert show class="mt-3 py-4 text-center">
            {{ $t('[[[Brak danych w podanym okresie czasu.]]]') }}
        </IdeoAlert>
    </div>
    <div v-else class="wrapper" :style="{ maxHeight: maxHeight }">
        <!-- Left side: employee names -->
        <div class="employees" :style="{ marginTop: `${isMonthsShown ? ROW_MIN_HEIGHT * 2 : ROW_MIN_HEIGHT}px` }">
            <div
                v-for="(height, idx) in rowHeightMap"
                :key="`emp-${idx}`"
                class="employee-row"
                :class="{ invisible: rows[idx]?.isGroup }"
                :style="{ transform: `translateY(${containerScrollTop * -1}px)`, height: `${height}px` }"
            >
                <div class="employee-cell">
                    <span class="employee-cell-text" :title="rows[idx]?.value">{{ rows[idx]?.value }}</span>
                </div>
            </div>
        </div>

        <!-- Right side: timetable -->
        <div ref="containerRef" class="timetable-wrapper" :style="timetableStyles">
            <!-- Widok normalny: wirtualizacja, scroll w bok -->
            <div ref="wrapperRef" class="timetable-container timetable-container-screen" @scroll="handleScroll">
                <div class="timetable-viewport" :style="{ width: `${viewportWidth}px`, height: `${viewportHeight}px` }">
                    <!-- Header -->
                    <div class="timetable-header">
                        <div v-if="isMonthsShown" class="timetable-row">
                            <div class="timetable-row-wrapper">
                                <div
                                    v-for="(m, i) in months"
                                    :key="`month-${i}-${m.year}`"
                                    class="timetable-cell timetable-cell-month"
                                    :style="{ width: `${m.days * COLUMN_WIDTH}px` }"
                                >
                                    {{ m.month }}
                                </div>
                            </div>
                        </div>

                        <div class="timetable-row">
                            <div class="timetable-row-wrapper" :style="{ transform: `translateX(${clampedShift * COLUMN_WIDTH}px)` }">
                                <div
                                    v-for="(u, i) in windowUnits"
                                    :key="`hdr-${clampedShift + i}-${u.date}`"
                                    v-tooltip.top="u.isHoliday ? getHolidayName(u.date.toString()) : undefined"
                                    class="timetable-cell timetable-cell-header"
                                    :class="{ active: u.active, weekend: u.isWeekend, holiday: u.isHoliday }"
                                >
                                    <span>{{ u.name }}</span>
                                    <span v-if="props.settings.timeScale !== TimeScale.Week">{{ u.value }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Content -->
                    <div v-if="rows.length" class="timetable-content">
                        <div class="timetable-visible-rows" :style="{ transform: `translateY(${topOffset}px)` }">
                            <div
                                v-for="(height, rIdx) in visibleRows"
                                :key="`row-${startScrollIndex + rIdx}`"
                                class="timetable-row"
                                :style="{ height: `${height}px` }"
                            >
                                <div v-if="rows[startScrollIndex + rIdx].isGroup" class="timetable-row-group" :style="{ width: `${Math.min(containerSize.width, viewportWidth)}px` }">
                                    {{ rows[startScrollIndex + rIdx].value }}
                                </div>
                                <div v-else class="timetable-row-wrapper" :style="{ transform: `translateX(${clampedShift * COLUMN_WIDTH}px)` }">
                                    <div
                                        v-for="(u, i) in windowUnits"
                                        :key="`day-${startScrollIndex + rIdx}-${clampedShift + i}-${u.date}`"
                                        class="timetable-cell timetable-cell-day"
                                        :class="{ 'holiday': u.isHoliday, 'weekend': u.isWeekend }"
                                    >
                                        <template v-if="documentsByDate.get(u.date)?.find(d => d.key === `${rows[startScrollIndex + rIdx].groupName}-${rows[startScrollIndex + rIdx].key}`)?.items?.length">
                                            <div
                                                v-for="(doc, docIndex) in documentsByDate.get(u.date)?.find(d => d.key === `${rows[startScrollIndex + rIdx].groupName}-${rows[startScrollIndex + rIdx].key}`)?.items"
                                                :key="`${doc.publicId}-${docIndex}`"
                                                class="timetable-cell-tooltip"
                                                :style="{ color: getTextColorForBackground(doc.color), background: getColor(doc.color) }"
                                            >
                                                <template v-if="focusedDocuments.includes(`${u.date}-${doc.publicId}-${doc.color}`)">
                                                    <RowActions
                                                        :id="doc.publicId"
                                                        ref="rowActionsRef"
                                                        :settings="{ color: doc.color, date: u.date }"
                                                        :disabled="readOnly"
                                                        :endpoint="`${licence}/${doc.publicId}/actions`"
                                                        :url="licence"
                                                        :style="{ color: getTextColorForBackground(doc.color), background: getColor(doc.color) }"
                                                        append-to-body
                                                        :except="[
                                                            'document-notifications-create',
                                                            'canManageParticipants',
                                                            'canUploadOneDrive',
                                                            'bpmn-set-targets',
                                                            'canSetPrivate',
                                                        ]"
                                                        class="rounded-0 border-0 btn-document"
                                                        :options="{ calendar: true, dropdown: { position: 'left' } }"
                                                    >
                                                        <template #button-content>
                                                            {{ doc.shortName || u.value }}
                                                        </template>
                                                        <template #default>
                                                            <IdeoDropdownItemButton @click="onRowClick(doc)">
                                                                <i class="fas fa-square-list"></i>
                                                                <span>{{ $t('[[[Szczegóły]]]') }}</span>
                                                            </IdeoDropdownItemButton>
                                                        </template>
                                                    </RowActions>
                                                </template>
                                                <template v-else>
                                                    <template v-if="getTooltip(doc)">
                                                        <div v-tooltip.top="getTooltip(doc)" @click="focusDocument(u.date, doc)">
                                                            {{ doc.shortName || u.value }}
                                                        </div>
                                                    </template>
                                                    <template v-else>
                                                        <div @click="focusDocument(u.date, doc)">
                                                            {{ doc.shortName || u.value }}
                                                        </div>
                                                    </template>
                                                </template>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div style="z-index: -1;">
                                                {{ u.value }}
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Widok do wydruku: jedna tabela dla wszystkich chunków, aby thead powtarzał się na każdej stronie -->
            <div class="timetable-container timetable-container-print">
                <div ref="printMeasureRef" class="print-measure"></div>
                <div class="print-page-wrapper">
                    <!-- Każdy chunk w osobnej tabeli z własnym thead - gwarantuje nagłówek na każdej stronie -->
                    <template v-for="(chunk, p) in printChunks" :key="`print-chunk-${p}`">
                        <table class="timetable-print-grid" :class="{ 'print-page-break-after': p < printChunks.length - 1 }">
                            <!-- Header dla każdej tabeli -->
                            <thead class="timetable-header">
                                <!-- Legenda -->
                                <tr v-if="props.legend && props.legend.length > 0" class="print-legend-row">
                                    <td class="employee-header-cell" :rowspan="isMonthsShown ? 4 : 3"></td>
                                    <td :colspan="18" class="print-legend-cell">
                                        <ul class="d-flex list-unstyled mb-0 legend">
                                            <li v-for="(status, index) in props.legend" :key="index" class="me-3">
                                                <div class="d-flex align-items-center">
                                                    <div :style="{ background: getColor(status.color) }" class="legend__item"></div>
                                                    <span>{{ status.value }}</span>
                                                </div>
                                            </li>
                                        </ul>
                                    </td>
                                </tr>
                                <!-- Zakres dat dla tego chunka -->
                                <tr class="print-page-header-row">
                                    <td v-if="!(props.legend && props.legend.length > 0)" class="employee-header-cell" :rowspan="isMonthsShown ? 3 : 2"></td>
                                    <td :colspan="18" class="print-page-header-cell">
                                        {{ getPageRangeLabel(chunk.units) }}
                                    </td>
                                </tr>
                                <!-- Etykieta miesiąca dla tego chunka z podziałem na miesiące -->
                                <tr v-if="isMonthsShown" class="timetable-row">
                                    <td v-if="!(props.legend && props.legend.length > 0)" class="employee-header-cell"></td>
                                    <td
                                        v-for="(range, rangeIdx) in getChunkMonthRanges(chunk.units)"
                                        :key="`month-range-${p}-${rangeIdx}`"
                                        :colspan="range.colspan"
                                        class="timetable-cell timetable-cell-month"
                                    >
                                        {{ range.month }}
                                    </td>
                                </tr>
                                <!-- Nagłówki kolumn dla tego chunka -->
                                <tr class="timetable-row">
                                    <td v-if="!(props.legend && props.legend.length > 0)" class="employee-header-cell"></td>
                                    <td
                                        v-for="(u, i) in chunk.units"
                                        :key="`print-hdr-${p}-${i}-${u.date}`"
                                        v-tooltip.top="u.isHoliday ? getHolidayName(u.date.toString()) : undefined"
                                        class="timetable-cell timetable-cell-header"
                                        :class="{ active: u.active, weekend: u.isWeekend, holiday: u.isHoliday, empty: (u as any).isEmpty }"
                                    >
                                        <span>{{ u.name }}</span>
                                        <span v-if="props.settings.timeScale !== TimeScale.Week">{{ u.value }}</span>
                                    </td>
                                </tr>
                            </thead>

                            <!-- Content dla tego chunka -->
                            <tbody v-if="rows.length" class="timetable-content">
                                <!-- Wiersze danych -->
                                <template
                                    v-for="(height, rIdx) in rowHeightMap"
                                    :key="`print-row-${p}-${rIdx}`"
                                >
                                    <tr
                                        v-if="rows[rIdx].isGroup"
                                        class="timetable-row"
                                        :style="{ height: `${height}px` }"
                                    >
                                        <td class="employee-cell" :class="{ invisible: rows[rIdx]?.isGroup }"></td>
                                        <td
                                            class="timetable-row-group"
                                            :style="{ width: `${chunk.units.length * COLUMN_WIDTH}px` }"
                                            :colspan="chunk.units.length"
                                        >
                                            {{ rows[rIdx].value }}
                                        </td>
                                    </tr>
                                    <tr
                                        v-else
                                        class="timetable-row"
                                        :style="{ height: `${height}px` }"
                                    >
                                        <td class="employee-cell">
                                            <span class="employee-cell-text" :title="rows[rIdx]?.value">{{ rows[rIdx]?.value }}</span>
                                        </td>
                                        <td
                                            v-for="(u, i) in chunk.units"
                                            :key="`print-day-${p}-${rIdx}-${i}-${u.date}`"
                                            class="timetable-cell timetable-cell-day"
                                            :class="{ holiday: u.isHoliday, weekend: u.isWeekend, empty: (u as any).isEmpty }"
                                        >
                                            <template v-if="printDocumentsByPage[p]?.get(u.date)?.find(d => d.key === `${rows[rIdx].groupName}-${rows[rIdx].key}`)?.items?.length">
                                                <template
                                                    v-for="(doc, docIndex) in printDocumentsByPage[p]?.get(u.date)?.find(d => d.key === `${rows[rIdx].groupName}-${rows[rIdx].key}`)?.items"
                                                    :key="`print-${p}-${doc.publicId}-${docIndex}`"
                                                >
                                                    <div
                                                        v-if="doc.shortName ?? u.value"
                                                        class="timetable-cell-tooltip"
                                                        :style="{ color: getTextColorForBackground(doc.color), background: getColor(doc.color) }"
                                                    >
                                                        <div>
                                                            {{ doc.shortName ?? u.value }}
                                                        </div>
                                                    </div>
                                                </template>
                                            </template>
                                            <template v-else>
                                                <div>
                                                    {{ u.value }}
                                                </div>
                                            </template>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.btn-document .dropdown, .btn-document .btn-group {
    height: 100%;
}
</style>

<style scoped>
.wrapper {
  display: flex;
  align-items: stretch;
  height: 100%;

  * {
    box-sizing: border-box;
  }
}

.btn-document {
    height: 100%;

    .dropdown, .btn-group {
        height: 100%;
    }
}

.timetable-wrapper {
  overflow: hidden;
  height: 100%;
  width: max-content;
}

.timetable-container-print {
  display: none;
}

.timetable-container {
  width: 100%;
  height: 100%;
  overflow: auto;
}

.timetable-header {
  position: sticky;
  top: 0;
  z-index: 9;
}

.timetable-row-group {
  position: sticky;
  left: 0;
  font-weight: 600;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border-bottom: 1px solid var(--bs-gray-300);
}

.timetable-row-wrapper {
  display: flex;
  align-items: stretch;
  width: fit-content;
  height: 100%;
}

.timetable-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--timetable-column-width);
  height: 100%;
  z-index: -1;
  border-bottom: 1px solid var(--bs-gray-300);
  border-right: 1px solid var(--bs-gray-300);
  overflow: hidden;
}

.timetable-cell.active {
  background: var(--bs-primary);
  color: var(--bs-white);
  border-color: var(--bs-primary);
}

.timetable-cell.weekend {
  background: var(--bs-gray-100);
  color: var(--bs-gray-500);
  font-weight: 400;
}

.timetable-cell.holiday {
  background-color: var(--bs-warning-bg-subtle);
  color: var(--bs-warning-text-emphasis);
  font-weight: 600;
}

.timetable-cell-header {
  height: var(--timetable-row-height);
  flex-direction: column;
  background: var(--bs-gray-200);
  line-height: 1.2;
  font-weight: 600;
}

.timetable-cell-month {
  background: var(--bs-body-bg);
  height: var(--timetable-row-height);
  font-weight: 700;
  text-transform: uppercase;
  border-top: 1px solid var(--bs-gray-300);

  &:first-child {
    border-left: 1px solid var(--bs-gray-300);
  }
}

.timetable-cell-day {
  display: flex;
  flex-direction: column;
}

.timetable-cell-tooltip {
    width: 100%;
    height: 100%;

    div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        cursor: pointer;
    }
}

.employees {
  overflow: hidden;
  min-width: 250px;
  flex: 1;
}

.employee-row {
  display: flex;
  width: 100%;
}

.employee-cell {
  display: flex;
  align-items: center;
  padding: 0 16px;
  flex: none;
  width: 100%;
  border-bottom: 1px solid var(--bs-gray-300);
  border-right: 1px solid var(--bs-gray-300);
  border-left: 1px solid var(--bs-gray-300);
}

.employee-cell-text {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.employee-row.invisible + .employee-row .employee-cell {
    border-top: 1px solid var(--bs-gray-300);
}

@media print {
    @page {
        size: A4 landscape;
        margin: 10mm;
    }

    .print-measure {
        width: 100%;
        height: 0;
        visibility: hidden;
    }

    html, body, #app {
        overflow: visible !important;
        height: auto !important;
        max-height: none !important;
    }

    #layout {
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
    }

    #layout #content {
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
    }

    .wrapper {
        height: auto;
        max-height: none !important;
    }

    .timetable-wrapper {
        width: 100% !important;
        height: auto !important;
        overflow: visible !important;
    }

    .timetable-container-screen {
        display: none !important;
    }

    .timetable-container-print {
        display: block !important;
        height: auto !important;
        overflow: visible !important;
    }

    .timetable-container-print .timetable-cell {
        z-index: unset !important;
    }

    .timetable-container {
        overflow: visible !important;
        height: auto !important;
    }

    .print-page-wrapper {
        height: auto !important;
        overflow: visible !important;
    }

    .timetable-header {
        position: static;
        top: auto;
    }

    .timetable-row {
        break-inside: avoid;
        page-break-inside: avoid;
    }

    .employees:not(.employees-print) {
        display: none;
    }

    .timetable-print-grid {
        display: table !important;
        width: 100% !important;
        border-collapse: collapse;
        border-spacing: 0;
        table-layout: fixed;
        border: 1px solid var(--bs-gray-300);
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        visibility: visible !important;
        opacity: 1 !important;
    }

    .timetable-print-grid thead tr:first-child td:first-child {
        border-top: 1px solid var(--bs-gray-300);
        border-left: 1px solid var(--bs-gray-300);
    }

    .timetable-print-grid thead {
        display: table-header-group !important;
        page-break-inside: avoid;
    }

    .timetable-print-grid thead tr {
        page-break-inside: avoid;
        break-inside: avoid;
    }

    .timetable-print-grid tbody {
        display: table-row-group;
    }

    /* Podział strony po każdej tabeli (oprócz ostatniej) */
    .timetable-print-grid.print-page-break-after {
        page-break-after: always !important;
        break-after: page !important;
    }

    /* Nagłówki chunków w tbody */
    .print-chunk-header-row,
    .print-chunk-month-row,
    .print-chunk-column-headers-row {
        page-break-inside: avoid;
    }

    .timetable-print-grid tbody {
        display: table-row-group;
    }

    .timetable-print-grid tr {
        display: table-row;
        page-break-inside: avoid;
    }

    .timetable-print-grid td {
        display: table-cell;
        vertical-align: middle;
        box-sizing: border-box;
    }

    /* Wiersze tabeli */
    .timetable-print-grid .timetable-row {
        height: auto;
        min-height: var(--timetable-row-height, 40px);
    }

    /* Lepsza czytelność w druku */
    .timetable-print-grid td {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }

    .print-legend-row {
        page-break-inside: avoid;
    }

    .print-legend-cell {
        padding: 10px 20px;
        border-bottom: 1px solid var(--bs-gray-300);
        border-right: 1px solid var(--bs-gray-300);
        background: var(--bs-body-bg);
    }

    .print-legend-cell .legend {
        margin: 0;
        padding: 0;
        justify-content: center;
    }

    .print-legend-cell .legend__item {
        width: 15px;
        min-width: 15px;
        height: 15px;
        margin-right: 6px;
    }

    .print-page-header-row {
        page-break-inside: avoid;
    }

    .print-page-header-cell {
        font-weight: 600;
        text-align: center;
        padding: 20px;
        border-bottom: 1px solid var(--bs-gray-400);
        border-right: 1px solid var(--bs-gray-300);
        font-size: 1rem;
        background: var(--bs-body-bg);
    }

    /* Kolumna employees w tabeli */
    .timetable-print-grid .employee-header-cell {
        width: 200px;
        min-width: 200px;
        max-width: 200px;
        border-bottom: 1px solid var(--bs-gray-300);
        border-right: 1px solid var(--bs-gray-300);
        border-top: 1px solid var(--bs-gray-300);
        border-left: 1px solid var(--bs-gray-300);
        padding: 0;
        vertical-align: middle;
    }

    .timetable-print-grid .employee-cell {
        display: table-cell;
        width: 200px;
        min-width: 200px;
        max-width: 200px;
        padding: 0 16px;
        border-bottom: 1px solid var(--bs-gray-300);
        border-right: 1px solid var(--bs-gray-300);
        border-left: 1px solid var(--bs-gray-300);
        vertical-align: middle;
        font-weight: 500;
    }

    .timetable-print-grid .employee-cell.invisible {
        border-left: 1px solid var(--bs-gray-300);
        border-right: 1px solid var(--bs-gray-300);
        background: transparent;
        padding: 0;
    }

    .timetable-print-grid .employee-cell-text {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        width: 100%;
        display: block;
    }

    /* Zachowaj style dla komórek tabeli w trybie drukowania */
    .timetable-print-grid .timetable-cell {
        display: table-cell;
        width: 40px;
        min-width: 40px;
        max-width: 40px;
        height: 100%;
        border-bottom: 1px solid var(--bs-gray-300);
        border-right: 1px solid var(--bs-gray-300);
        padding: 0;
        vertical-align: middle;
        text-align: center;
    }

    /* Style dla nagłówków komórek */
    .timetable-print-grid .timetable-cell-header {
        background: var(--bs-gray-200);
        font-weight: 600;
        line-height: 1.2;
        padding: 8px 4px;

        span {
            display: block;
        }
    }

    .timetable-print-grid .timetable-cell-header.active {
        background: var(--bs-primary);
        color: var(--bs-white);
        border-color: var(--bs-primary);
    }

    .timetable-print-grid .timetable-cell-header.weekend {
        background: var(--bs-gray-100);
        color: var(--bs-gray-500);
        font-weight: 400;
    }

    .timetable-print-grid .timetable-cell-header.holiday {
        background-color: var(--bs-warning-bg-subtle);
        color: var(--bs-warning-text-emphasis);
        font-weight: 600;
    }

    /* Style dla komórek miesiąca */
    .timetable-print-grid .timetable-cell-month {
        background: var(--bs-body-bg);
        font-weight: 700;
        text-transform: uppercase;
        border-top: 1px solid var(--bs-gray-300);
        padding: 8px 4px;
    }

    /* Style dla komórek dni */
    .timetable-print-grid .timetable-cell-day {
        padding: 4px;
    }

    .timetable-print-grid .timetable-cell-day.weekend {
        background: var(--bs-gray-100);
        color: var(--bs-gray-500);
    }

    .timetable-print-grid .timetable-cell-day.holiday {
        background-color: var(--bs-warning-bg-subtle);
        color: var(--bs-warning-text-emphasis);
        font-weight: 600;
    }

    /* Style dla pustych kolumn */
    .timetable-print-grid .timetable-cell.empty {
        background: var(--bs-gray-100) !important;
    }

    .timetable-print-grid .timetable-cell-header.empty {
        background: var(--bs-gray-100) !important;
    }

    /* Style dla grup wierszy */
    .timetable-print-grid .timetable-row-group {
        border-bottom: 1px solid var(--bs-gray-300);
        border-right: 1px solid var(--bs-gray-300);
        padding: 8px 16px;
        vertical-align: middle;
        font-weight: 600;
        text-transform: uppercase;
        text-align: center;
    }

    /* Style dla tooltipów w komórkach */
    .timetable-print-grid .timetable-cell-tooltip {
        min-height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 2px auto;
        border-radius: 2px;
    }

    .timetable-print-grid .timetable-cell-tooltip div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        min-height: 20px;
        font-size: 0.85rem;
        line-height: 1.2;
    }

    .print-page-wrapper {
        width: 100%;
        display: block;
    }
}
</style>

<style>
@media print {
    #layout #content > #view > .scroll,
    .scroll {
        overflow-x: unset !important;
        overflow-y: unset !important;
        height: unset !important;
    }

    #layout #content > #view {
        height: 100% !important;
        overflow: unset !important;
    }

    .card .timetable-years, .card .timetable-periods, .card .timetable-time-ranges, .card .timetable-legend {
        display: none !important;
    }
}
</style>
